import { DateTime } from 'luxon'

import { toHumanReadableString } from '@/utils/time'

import SATTestQ from '@/queries/SATTestQ'
import SeriesQ from '@/queries/SeriesQ'
import SeriesRegistrationQ from '@/queries/SeriesRegistrationQ'
import SeriesReservationQ from '@/queries/SeriesReservationQ'
import UserQ from '@/queries/UserQ'
import WorldMemberQ from '@/queries/WorldMemberQ'
import WorldQ from '@/queries/WorldQ'
import { SQL } from '@/client/query'
import type { PlatformBannerId } from '@/client/services/banners'
import { PlatformDevice } from '@/client/services/platform/constants'
import { SAT_BOOTCAMP_PROGRAM_SLUG } from '@/client/services/programs/constants'
import {
  getTutorOfferingPageUrlByWorld,
  needsConsentForProgram,
} from '@/client/services/programs/helpers'
import composeRichText from '@/client/services/richText/composeRichText'
import type { SerializedRichText } from '@/client/services/richText/types'
import {
  SAT_BOOTCAMP_RECENTLY_ACTIVE_MONTHS,
  SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS,
} from '@/client/services/satBootcamp/constants'
import { getDashboardProgramsUrl, getRequestParentConsentUrl } from '@/client/services/url'
import { hasUserRole } from '@/client/services/user'
import { SAT_BOOTCAMP_WORLD_SLUG, SAT_PREP_WORLD_SLUG } from '@/client/services/worlds/constants'
import getUserLevelInWorld from '@/client/services/worlds/getUserLevelInWorld'
import isLevelAtLeastMinLevel from '@/client/services/worlds/isLevelAtLeastMinLevel'

import { makeBannerDataLoader } from './helpers'

export default makeBannerDataLoader({
  name: 'sat-bootcamp',
  load(context) {
    if (!hasUserRole(context.user, 'user:approved')) {
      return null
    }

    const isUserTutor = hasUserRole(context.user, 'user:tutor')

    const upcomingTest = context.query.one(
      SATTestQ.where(
        {
          limit: 1,
        },
        SQL`${SATTestQ.hasBeenReleasedToTutors()} AND ${SATTestQ.learnerRegistrationHasNotPassed()}`
      )
    )
    const upcomingTestId = upcomingTest(SATTestQ.id)
    const upcomingTestDate = DateTime.fromISO(`${upcomingTest(SATTestQ.test_date)}`)
    const upcomingSeriesCreationDeadline = DateTime.fromISO(
      `${upcomingTest(SATTestQ.series_creation_deadline)}`
    )

    const upcomingBootcampReservation = context.query.one(
      SeriesReservationQ.getActiveSATBootcampSelfReservations({ userId: context.user.id, limit: 1 })
    )
    const reservationExpiredAt = upcomingBootcampReservation(SeriesReservationQ.expired_at)
    const hasUpcomingReservation = reservationExpiredAt != null
    const reservationTimeLeftLong =
      reservationExpiredAt == null
        ? null
        : toHumanReadableString(DateTime.fromISO(`${reservationExpiredAt}`).diffNow())

    const reservationTimeLeftShort =
      reservationExpiredAt == null
        ? null
        : toHumanReadableString(DateTime.fromISO(`${reservationExpiredAt}`).diffNow(), {
            omitSecondary: true,
          })

    const now = DateTime.fromISO(context.requestTimestamp, { zone: 'utc' })

    const existsUpcomingTestToTutor = upcomingTestId != null && now < upcomingSeriesCreationDeadline

    const existsUpcomingTestToRegister = upcomingTestId != null && now < upcomingTestDate

    const isTutorInWorld = isLevelAtLeastMinLevel({
      level:
        getUserLevelInWorld({ user: context.user, worldSlug: SAT_BOOTCAMP_WORLD_SLUG }) ?? 'member',
      minLevel: 'tutor',
    })

    const activeTutorBootcamp = context.query.one(
      SeriesQ.where(
        { limit: 1 },
        SQL`${SeriesQ.hasAnyTemplateSlug(SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS)}
          AND NOT ${SeriesQ.isClosed()}
          AND ${SeriesQ.isHost({ userId: context.user.id })}`
      )
    )
    const hasActiveBootcamp = activeTutorBootcamp(SeriesQ.id) != null

    const me = context.query.one(UserQ.where({ id: context.user.id }))
    const isRecentlyActive =
      me(UserQ.hasBeenOnlineInThePastNMonths(SAT_BOOTCAMP_RECENTLY_ACTIVE_MONTHS)) ?? false
    const parentConsentCompletedAt = me(UserQ.parent_consent_completed_at)

    // Match both SAT worlds to mirror the learner recruitment email scheduler
    const satLearnerWorldMembership = context.query.one(
      WorldMemberQ.where(
        {
          userId: context.user.id,
          limit: 1,
          include: [
            {
              association: 'World',
              required: true,
              where: SQL`${WorldQ.hasAnySlug([SAT_BOOTCAMP_WORLD_SLUG, SAT_PREP_WORLD_SLUG])}`,
            },
          ],
        },
        SQL`${WorldMemberQ.hasHelpIntent('receive-help')}`
      )
    )
    const isInterestedLearnerMember = satLearnerWorldMembership(WorldMemberQ.id) != null

    const isRegisteredForActiveBootcamp =
      me(
        SQL<boolean>`EXISTS(SELECT 1 ${SeriesRegistrationQ.where({
          userId: UserQ.id,
          include: [
            {
              association: 'Series',
              required: true,
              where: SQL`${SeriesQ.hasAnyTemplateSlug(SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS)} AND NOT ${SeriesQ.isClosed()}`,
            },
          ],
        })})`
      ) ?? false

    const isRecentlyActiveLearner = isRecentlyActive && isInterestedLearnerMember

    const { needsParentConsent } = needsConsentForProgram(SAT_BOOTCAMP_PROGRAM_SLUG)

    const hostSATBootcampUrl = getTutorOfferingPageUrlByWorld(SAT_BOOTCAMP_WORLD_SLUG)

    const bannerIdMonth = upcomingTestDate?.toFormat('LLL').toLowerCase() ?? ''
    const bannerIdYear = upcomingTestDate?.year ?? 0

    const testMonthLong = upcomingTestDate?.toFormat('LLLL')
    const testMonthShort = upcomingTestDate?.toFormat('LLL')

    const seriesCreationDeadlineLong = upcomingSeriesCreationDeadline?.toFormat('LLLL d')
    const seriesCreationDeadlineShort = upcomingSeriesCreationDeadline?.toFormat('LLL d')

    let id: PlatformBannerId
    let longText: SerializedRichText
    let shortText: SerializedRichText
    let shouldRender = true
    let sticky = false
    let link: string

    // TUTOR CASES
    if (isTutorInWorld) {
      if (hasActiveBootcamp || !existsUpcomingTestToTutor) {
        return null
      }
      id = `sat-bootcamp-${bannerIdMonth}-${bannerIdYear}-tutor-host-nudge`
      link = hostSATBootcampUrl

      longText = composeRichText(ctx =>
        ctx.paragraph(
          `We are expecting higher demand for the ${testMonthLong} SAT Bootcamp! Help learners by publishing a bootcamp by ${seriesCreationDeadlineLong}! It only takes 2 minutes. `,
          ctx.linkInternal(link, 'Create My Bootcamp')
        )
      )

      shortText = composeRichText(ctx =>
        ctx.paragraph(
          'Help learners! ',
          ctx.linkInternal(link, 'Publish'),
          ` a ${testMonthShort} SAT Bootcamp by ${seriesCreationDeadlineShort}!`
        )
      )
    }
    // ACTIVE LEARNER CASES
    else if (
      isRecentlyActiveLearner &&
      !isRegisteredForActiveBootcamp &&
      !hasUpcomingReservation &&
      upcomingTestId != null
    ) {
      id = `sat-bootcamp-${bannerIdMonth}-${bannerIdYear}-learner-register-nudge`
      link = getDashboardProgramsUrl({ programSlug: SAT_BOOTCAMP_PROGRAM_SLUG })

      longText = composeRichText(ctx =>
        ctx.paragraph(
          `Register for the ${testMonthLong} SAT Bootcamp before spots fill up! `,
          ctx.linkInternal(link, 'Register Now')
        )
      )
      shortText = composeRichText(ctx =>
        ctx.paragraph(
          ctx.linkInternal(link, 'Register'),
          ` for the ${testMonthShort} SAT Bootcamp before spots fill up!`
        )
      )
    } else if (needsParentConsent && hasUpcomingReservation && parentConsentCompletedAt == null) {
      id = `sat-bootcamp-${bannerIdMonth}-${bannerIdYear}-learner-consent-nudge`
      link = getRequestParentConsentUrl()

      sticky = true

      longText = composeRichText(ctx =>
        reservationTimeLeftLong == null
          ? ctx.paragraph(
              'Please ask your parent to ',
              ctx.linkInternal(link, 'complete the consent form'),
              '!'
            )
          : ctx.paragraph(
              'Please ask your parent to ',
              ctx.linkInternal(link, 'complete the consent form'),
              ` before your spot expires ${reservationTimeLeftLong}.`
            )
      )
      shortText = composeRichText(ctx =>
        reservationTimeLeftShort == null
          ? ctx.paragraph(
              ctx.linkInternal(link, 'Finish parent consent'),
              ' before attending your bootcamp!'
            )
          : ctx.paragraph(
              'Have your parent ',
              ctx.linkInternal(link, 'finish consent'),
              ` before your spot expires ${reservationTimeLeftShort}.`
            )
      )
    }
    // GENERIC CASES
    else {
      id = `sat-bootcamp-${bannerIdMonth}-${bannerIdYear}`
      link = isUserTutor
        ? getTutorOfferingPageUrlByWorld(SAT_BOOTCAMP_WORLD_SLUG)
        : getDashboardProgramsUrl({ programSlug: SAT_BOOTCAMP_PROGRAM_SLUG })

      longText = composeRichText(ctx =>
        isUserTutor
          ? ctx.paragraph(
              'Have you scored well on the SAT? Sign up to teach a 4-week SAT Bootcamp. ',
              ctx.linkInternal(link, 'Learn More')
            )
          : ctx.paragraph(
              'Preparing for your next SAT? Sign up for a free 4-week SAT Bootcamp. ',
              ctx.linkInternal(link, 'Learn More')
            )
      )
      shortText = composeRichText(ctx =>
        isUserTutor
          ? ctx.paragraph(
              'Scored well on the SAT? ',
              ctx.linkInternal(link, 'Teach a free 4-week bootcamp.')
            )
          : ctx.paragraph(
              'Preparing for your next SAT? ',
              ctx.linkInternal(link, 'Join a free 4-week bootcamp.')
            )
      )
      shouldRender = isUserTutor ? existsUpcomingTestToTutor : existsUpcomingTestToRegister
    }

    if (!shouldRender) {
      return null
    }

    if (context.user.platformDevice === PlatformDevice.DESKTOP) {
      return {
        slug: id,
        sticky,
        content: longText,
      }
    }

    return {
      slug: id,
      sticky,
      link,
      content: shortText,
    }
  },
})
